/**
 * GIWAHS — Membership (public) — Step 9
 * ---------------------------------------------------------------------
 * Public-facing membership tier landing. Anonymous-readable. Shows:
 *   - Live allocation gauge (Wave 1/2/3 capacity utilisation)
 *   - Three founder-tier cards with founder vs standard pricing
 *   - Three standard-tier cards (always available)
 *   - CTAs: Become a Founding Partner | Join Waitlist | Become a Member
 *
 * State machine:
 *   1. UI flag OFF  -> renders "ComingSoon" placeholder
 *   2. UI flag ON, backend router OFF (404)  -> renders "ComingSoon"
 *   3. Backend OK, allocations data present  -> renders live page
 *   4. Backend OK, allocations error         -> renders fallback with seed values
 *
 * No business logic — calls into membershipsClient only.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  membershipsClient,
  isMembershipsUiEnabled,
  TIER_META,
  FOUNDER_TIERS,
  STANDARD_TIERS,
  fmtUSD,
} from '../lib/membershipsClient';

const GOLD = '#9A7B2A';

// ──────────────────────────────────────────────────────────────────────
// Allocation gauge — visual capacity bar per wave.
// ──────────────────────────────────────────────────────────────────────
const AllocationGauge = ({ wave }) => {
  const claimed   = Math.max(0, Math.min(Number(wave?.claimed_count || 0), Number(wave?.public_capacity || 0)));
  const capacity  = Math.max(0, Number(wave?.public_capacity || 0));
  const remaining = Math.max(0, capacity - claimed);
  const pct       = capacity ? Math.round((claimed / capacity) * 100) : 0;
  const open      = !!wave?.is_open;

  return (
    <div data-testid={`wave-gauge-${wave?.wave || 'x'}`} className="border border-neutral-900 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="label-overline tracking-wider">Wave {wave?.wave}</div>
        <div className={`label-overline tracking-wider ${open ? 'text-neutral-900' : 'text-neutral-400'}`}>
          {open ? '● Open' : '○ Locked'}
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-3">
        <div className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '2rem', lineHeight: 1 }}>
          {remaining}
        </div>
        <div className="text-sm text-neutral-500">of {capacity} remaining</div>
      </div>
      <div className="mt-4 h-1 w-full bg-neutral-100">
        <div className="h-1 bg-neutral-900" style={{ width: `${pct}%`, transition: 'width 600ms ease-out' }} data-testid={`wave-gauge-${wave?.wave}-fill`} />
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-wider text-neutral-500 font-mono">
        {pct}% allocated · {wave?.reserved_for_upgrades || 0} reserved upgrades
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Tier card — used for both founder and standard tiers.
// ──────────────────────────────────────────────────────────────────────
const TierCard = ({ tierKey, allocation, onChoose, busy, ctaLabel, ctaSubtle }) => {
  const meta = TIER_META[tierKey];
  if (!meta) return null;
  const remaining = allocation
    ? Math.max(0, Number(allocation.public_capacity || 0) - Number(allocation.claimed_count || 0))
    : null;
  const soldOut = meta.founder && allocation && remaining <= 0;
  const lifetimeSaving = meta.founder && meta.standard_price_usd && meta.founder_price_usd
    ? (meta.standard_price_usd - meta.founder_price_usd) * 10
    : null;

  return (
    <div data-testid={`tier-card-${tierKey}`} className={`border ${meta.founder ? 'border-neutral-900' : 'border-neutral-300'} bg-white p-6 flex flex-col`}>
      <div className="flex items-center justify-between">
        <div className="label-overline tracking-wider" style={meta.founder ? { color: GOLD } : {}}>
          {meta.founder ? '/ Founding Partner' : '/ Standard Membership'}
        </div>
        {meta.founder && allocation && (
          <div className="font-mono text-[10px] uppercase tracking-wider text-neutral-500">
            {soldOut ? 'Sold out · waitlist open' : 'Applications open'}
          </div>
        )}
      </div>
      <div className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.05 }}>
        {meta.label}
      </div>

      {meta.founder ? (
        <>
          <div className="mt-5 flex items-baseline gap-3">
            <div className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '2.5rem', lineHeight: 1 }}>
              {fmtUSD(meta.founder_price_usd)}
            </div>
            <div className="text-sm text-neutral-500">per year</div>
          </div>
          <div className="mt-1 text-sm text-neutral-500">
            <span className="line-through">{fmtUSD(meta.standard_price_usd)}</span> · standard price
          </div>
          {lifetimeSaving && (
            <div className="mt-3 border border-neutral-200 bg-neutral-50 p-3">
              <div className="label-overline">10-Year Lifetime Saving</div>
              <div className="mt-1 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '1.25rem' }}>
                {fmtUSD(lifetimeSaving)}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-neutral-500 font-mono">
                Price-locked 10 years from activation
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mt-5 flex items-baseline gap-3">
          <div className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '2.5rem', lineHeight: 1 }}>
            {fmtUSD(meta.standard_price_usd)}
          </div>
          <div className="text-sm text-neutral-500">per year</div>
        </div>
      )}

      <div className="mt-6 flex-1 text-sm leading-relaxed text-neutral-700">
        {meta.family === 'gold' && 'Verified buyer access · saved factories · curated RFQ routing · founder badge.'}
        {meta.family === 'elite' && 'Everything in Gold · priority RFQ queue · 3-tier verification · early access to founder-only intelligence reports.'}
        {meta.family === 'enterprise' && 'Everything in Elite · dedicated sourcing lead · custom reporting · multi-team organisation access.'}
      </div>

      <button
        type="button"
        data-testid={`tier-card-${tierKey}-cta`}
        disabled={busy || soldOut === null}
        onClick={() => onChoose?.({ tier: tierKey, soldOut })}
        className={`mt-6 px-5 py-3 text-sm uppercase tracking-wider font-mono ${meta.founder ? 'bg-neutral-900 text-white' : 'border border-neutral-900 bg-white text-neutral-900'} disabled:opacity-50 hover:opacity-90 transition-opacity`}
      >
        {busy ? 'Working…' : (ctaLabel || (soldOut ? 'Join waitlist' : (meta.founder ? 'Become a Founding Partner' : 'Become a Member')))}
        <span className="ml-1">→</span>
      </button>
      {ctaSubtle && <div className="mt-2 text-[11px] uppercase tracking-wider text-neutral-500 font-mono">{ctaSubtle}</div>}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Coming soon state (UI flag off OR backend router off)
// ──────────────────────────────────────────────────────────────────────
const ComingSoon = ({ reason }) => (
  <section data-testid="membership-coming-soon" className="mx-auto max-w-3xl px-5 py-24 text-center md:py-32">
    <div className="label-overline" style={{ color: GOLD }}>/ Founding Partner Programme · pre-launch</div>
    <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem,6vw,4.5rem)', lineHeight: 0.95 }}>
      The Founding Partner programme is opening soon.
    </h1>
    <p className="mt-6 text-neutral-700 text-lg leading-relaxed">
      500 organisations. A contractual 10-year price-lock from activation. Permanent founder status. Open across three release waves with strict allocation gates.
    </p>
    <p className="mt-2 text-[11px] uppercase tracking-wider text-neutral-400 font-mono">
      Mode: {reason || 'pre-launch'}
    </p>
    <a href="/contact" className="mt-8 inline-block px-6 py-3 text-sm uppercase tracking-wider font-mono bg-neutral-900 text-white" data-testid="membership-coming-soon-contact-cta">
      Get an early invitation →
    </a>
  </section>
);

// ──────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────
const Membership = () => {
  const { isAuthenticated } = useAuth();
  const navigate            = useNavigate();
  const [state, setState]   = useState({ status: 'loading' }); // loading | disabled | ready | error
  const [busy, setBusy]     = useState(null); // tier currently being checked-out

  useEffect(() => {
    if (!isMembershipsUiEnabled()) {
      setState({ status: 'disabled', reason: 'ui_flag_off' });
      return;
    }
    let cancelled = false;
    (async () => {
      const r = await membershipsClient.listAllocations();
      if (cancelled) return;
      if (r?.disabled) {
        setState({ status: 'disabled', reason: r.reason || 'router_disabled' });
      } else if (r?.ok && Array.isArray(r.allocations)) {
        setState({ status: 'ready', allocations: r.allocations });
      } else {
        setState({ status: 'ready', allocations: [], degraded: true });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const allocationByTier = (tier) => {
    const all = state.allocations || [];
    // We surface "the most permissive wave for this tier" — i.e. the
    // currently-open wave. If no wave is open we fall back to wave 1.
    return all.find(a => a.tier === tier && a.is_open) || all.find(a => a.tier === tier) || null;
  };

  const wavesGrouped = () => {
    const all = state.allocations || [];
    const byWave = new Map();
    for (const a of all) {
      if (!byWave.has(a.wave)) byWave.set(a.wave, { wave: a.wave, is_open: a.is_open, public_capacity: 0, claimed_count: 0, reserved_for_upgrades: 0 });
      const w = byWave.get(a.wave);
      w.public_capacity       += Number(a.public_capacity || 0);
      w.claimed_count         += Number(a.claimed_count || 0);
      w.reserved_for_upgrades += Number(a.reserved_for_upgrades || 0);
      w.is_open                = w.is_open || a.is_open;
    }
    return Array.from(byWave.values()).sort((a, b) => a.wave - b.wave);
  };

  const handleChoose = async ({ tier, soldOut }) => {
    if (!isAuthenticated) {
      navigate(`/signup?next=${encodeURIComponent('/membership')}`);
      return;
    }
    setBusy(tier);
    try {
      if (soldOut) {
        const wl = await membershipsClient.joinWaitlist({ tier });
        if (wl?.ok) {
          toast.success(`You're on the ${TIER_META[tier]?.label} waitlist.`);
        } else if (wl?.code === 'forbidden') {
          toast.error(wl.detail || 'You do not have permission to join this waitlist.');
        } else {
          toast.error('Could not join the waitlist. Please try again.');
        }
        return;
      }
      const r = await membershipsClient.createCheckoutSession({
        tier,
        success_url: `${window.location.origin}/founder/dashboard?from=checkout`,
        cancel_url:  `${window.location.origin}/membership?cancelled=1`,
      });
      if (r?.ok && r.session_url) {
        window.location.href = r.session_url;
      } else if (r?.reason === 'waitlist_required') {
        toast.info('Allocations for this tier are currently held. Adding you to the waitlist…');
        const wl = await membershipsClient.joinWaitlist({ tier });
        if (wl?.ok) toast.success(`You're on the ${TIER_META[tier]?.label} waitlist.`);
      } else {
        toast.error(r?.detail || 'Checkout could not start. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  // Render path
  if (state.status === 'loading') {
    return (
      <div data-testid="membership-loading" className="mx-auto max-w-5xl px-5 py-32 text-center">
        <div className="label-overline text-neutral-500">Loading membership programme…</div>
        <div className="mt-3 h-px w-24 mx-auto bg-neutral-300 animate-pulse" />
      </div>
    );
  }
  if (state.status === 'disabled') {
    return (
      <>
        <Helmet>
          <title>Founding Partner Programme · GIWAHS</title>
          <meta name="description" content="GIWAHS Founding Partner programme — a contractual 10-year price-lock from activation across Gold, Elite, and Enterprise tiers." />
        </Helmet>
        <ComingSoon reason={state.reason} />
      </>
    );
  }

  const waves = wavesGrouped();

  return (
    <>
      <Helmet>
        <title>Founding Partner Programme · GIWAHS</title>
        <meta name="description" content="A contractual 10-year price-lock from activation. Permanent Founding Partner status across Gold, Elite, and Enterprise tiers." />
      </Helmet>

      {/* Hero */}
      <section data-testid="membership-hero" className="border-b border-neutral-900 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24 grid gap-10 md:grid-cols-[2fr_1fr] items-end">
          <div>
            <div className="label-overline" style={{ color: GOLD }}>/ Founding Partner Programme</div>
            <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem,6vw,5rem)', lineHeight: 0.95 }}>
              A worldwide-limited window.
              <br className="hidden md:block" />
              Permanent founder status. A 10-year price-lock from activation.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-700">
              Lock founder pricing for a decade. Reserve permanent Founding Partner status on your factory or buyer profile. Application-based allocation — never resold.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="border border-neutral-900 bg-neutral-900 text-white p-5">
              <div className="label-overline text-neutral-300">/ Lifetime</div>
              <div className="mt-2 font-display tracking-tighter text-white" style={{ fontWeight: 700, fontSize: '2rem', lineHeight: 1 }}>
                Founding Partner
              </div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-wider text-neutral-300">
                Gold · Elite · Enterprise
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Allocation gauges */}
      <section className="mx-auto max-w-6xl px-5 py-12">
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(1.5rem,3vw,2rem)' }}>
            Live allocation
          </h2>
          {state.degraded && (
            <div className="font-mono text-[11px] uppercase tracking-wider text-neutral-500" data-testid="membership-degraded">
              Showing wave seed data · live counter offline
            </div>
          )}
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="membership-gauges">
          {(waves.length ? waves : [{ wave: 1, is_open: true, public_capacity: 170, claimed_count: 0, reserved_for_upgrades: 0 }, { wave: 2, is_open: false, public_capacity: 165, claimed_count: 0, reserved_for_upgrades: 0 }, { wave: 3, is_open: false, public_capacity: 165, claimed_count: 0, reserved_for_upgrades: 0 }]).map(w => (
            <AllocationGauge key={w.wave} wave={w} />
          ))}
        </div>
      </section>

      {/* Founder tiers */}
      <section className="mx-auto max-w-6xl px-5 py-12 border-t border-neutral-200">
        <div className="label-overline" style={{ color: GOLD }}>/ Founder tiers · price-locked 10 years from activation</div>
        <h2 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(1.75rem,4vw,2.5rem)' }}>
          Choose your founder tier.
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3" data-testid="founder-tier-grid">
          {FOUNDER_TIERS.map(t => (
            <TierCard key={t} tierKey={t} allocation={allocationByTier(t)} onChoose={handleChoose} busy={busy === t} />
          ))}
        </div>
      </section>

      {/* Standard tiers */}
      <section className="mx-auto max-w-6xl px-5 py-12 border-t border-neutral-200">
        <div className="label-overline">/ Standard membership · open year-round</div>
        <h2 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(1.5rem,3vw,2rem)' }}>
          Prefer the standard route?
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3" data-testid="standard-tier-grid">
          {STANDARD_TIERS.map(t => (
            <TierCard key={t} tierKey={t} onChoose={handleChoose} busy={busy === t} ctaSubtle="No founder benefits · standard pricing forever" />
          ))}
        </div>
      </section>

      {/* FAQ — strict, short, no marketing fluff */}
      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="label-overline">/ Programme rules</div>
        <div className="mt-4 space-y-5 text-sm leading-relaxed text-neutral-700">
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">Founder number</div>
            <div className="mt-1">Assigned only after four gates pass: payment · activation · onboarding · verification submission. Numbers are permanent and non-transferable.</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">Price-lock</div>
            <div className="mt-1">Your founder rate is locked for 10 years from activation. Standard tier prices may rise; your founder rate cannot.</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">Allocations</div>
            <div className="mt-1">170 / 165 / 165 across waves 1 / 2 / 3. Each wave unlocks only after the prior wave reaches 80% sell-through. Reserved upgrade capacity is held inside each wave.</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">Waitlist</div>
            <div className="mt-1">If a tier is sold out you can join the FIFO waitlist. When an opening becomes available you have 72 hours to claim before it passes to the next organisation in queue.</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">Downgrades</div>
            <div className="mt-1">Founder → Standard downgrade permanently forfeits founder number, founder badge, and price-lock. Typed confirmation required.</div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Membership;
